import { useEffect } from 'react'
import axios from 'axios'

/**
 * SiteChatWidget — mounts the marketing-site assistant on ai.reright.io. Runtime
 * config (GET /api/v1/site/chat-widget → { botId, convaiAgentId }) picks the mode,
 * so an admin/agent flips it live with no SPA rebuild:
 *
 *   • convaiAgentId set → the ElevenLabs Conversational-AI VOICE agent — the exact
 *     interactive "talk to us" widget reright.io runs as "Aria" (voice + text, greets,
 *     drives to book-a-demo). Backed by the HOUSE_CONVAI_AGENT_ID platform setting.
 *   • else botId set → our own text Q&A bubble (public/widget.js → the rate-limited,
 *     domain-locked /api/v1/widget/:botId/chat endpoint). Backed by HOUSE_CHAT_BOT_ID.
 *   • neither → nothing injected (dormant + safe).
 *
 * Mounted from SiteFooter → every marketing page.
 */
export default function SiteChatWidget() {
  useEffect(() => {
    let alive = true
    let cleanupConvai = null

    axios.get('/api/v1/site/chat-widget')
      .then(({ data }) => {
        if (!alive || !data) return
        const convaiAgentId = data.convaiAgentId
        const botId = data.botId
        // Voice agent takes priority — this is the reright.io-style interactive widget.
        if (convaiAgentId) { cleanupConvai = mountConvai(convaiAgentId); return }
        if (botId) mountTextWidget(botId)
      })
      .catch(() => { /* no widget configured — render nothing */ })

    return () => { alive = false; if (cleanupConvai) cleanupConvai() }
  }, [])
  return null
}

// ── Text widget (our own Answers product) ──────────────────────────────────────
function mountTextWidget(botId) {
  if (document.getElementById('rr-house-widget')) return
  const s = document.createElement('script')
  s.id = 'rr-house-widget'
  s.src = '/widget.js'
  s.async = true
  s.setAttribute('data-bot', botId)
  document.body.appendChild(s)
}

// ── ElevenLabs Conversational-AI voice widget (mirrors reright.io's ConvaiWidget) ─
// ElevenLabs exposes no "collapse" API and only conversationStarted/Ended events, so
// to shrink back to the bubble we remove + recreate the element. We recreate when a
// call ends and when the user clicks outside an open (non-call) widget; an in-call
// guard ensures we never tear down an active conversation. Returns a cleanup fn.
function mountConvai(agentId) {
  const mount = document.createElement('div')
  mount.id = 'rr-convai-mount'
  document.body.appendChild(mount)

  if (!document.getElementById('convai-embed-script')) {
    const s = document.createElement('script')
    s.id = 'convai-embed-script'
    s.src = 'https://unpkg.com/@elevenlabs/convai-widget-embed'
    s.async = true
    s.type = 'text/javascript'
    document.body.appendChild(s)
  }

  let el = null
  let inCall = false
  let expanded = false
  let endTimer
  let idleTimer
  const IDLE_MS = 3 * 60 * 1000 // no activity for 3 min while open → shrink back to the bubble
  const resetIdle = () => {
    clearTimeout(idleTimer)
    idleTimer = setTimeout(() => { if (expanded && !inCall) recreate() }, IDLE_MS)
  }

  // The chatting visitor's own system time + timezone (read from THEIR browser),
  // handed to the agent so it greets by time-of-day, resolves relative dates
  // ("tomorrow at 3"), and books in their timezone by default.
  const timeVars = () => {
    try {
      const now = new Date()
      const tz = (Intl.DateTimeFormat().resolvedOptions().timeZone) || ''
      return {
        visitor_timezone: tz,
        visitor_tz_offset_min: String(-now.getTimezoneOffset()), // minutes east of UTC
        visitor_local_time: now.toLocaleString(undefined, {
          weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
          hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
        }),
        visitor_local_iso: now.toISOString(),
      }
    } catch {
      return { visitor_timezone: '', visitor_tz_offset_min: '', visitor_local_time: '', visitor_local_iso: '' }
    }
  }

  // Identity handed to the agent as dynamic variables. Public marketing pages are
  // guest-facing by default; a signed-in visitor is enriched best-effort below.
  let identity = {
    signed_in: 'false',
    user_name: 'there',
    user_full_name: '',
    user_email: '',
    tenant_id: '',
    tenant_name: '',
    has_workspace: 'false',
    ...timeVars(),
  }

  const clearWidgetCache = () => {
    try {
      ;[localStorage, sessionStorage].forEach((store) => {
        for (let i = store.length - 1; i >= 0; i--) {
          const k = store.key(i)
          if (k && /eleven|convai/i.test(k)) store.removeItem(k)
        }
      })
    } catch { /* noop */ }
  }

  const create = () => {
    el = document.createElement('elevenlabs-convai')
    // Public embed via agent-id (the ElevenLabs widget requires a public agent). A
    // signed-URL + private-agent lock isn't possible with the embed widget — that
    // needs a custom SDK UI, for which the origin-gated /site/convai-signed-url
    // endpoint is already in place.
    el.setAttribute('agent-id', agentId)
    el.setAttribute('dynamic-variables', JSON.stringify(identity))
    ;['conversationStarted', 'elevenlabs-convai:call'].forEach((evt) =>
      el.addEventListener(evt, onStart)
    )
    ;['conversationEnded', 'elevenlabs-convai:call-ended', 'call-ended', 'disconnect'].forEach((evt) =>
      el.addEventListener(evt, onEnd)
    )
    mount.appendChild(el)
  }

  const recreate = () => {
    try { el && el.remove() } catch { /* noop */ }
    clearWidgetCache()
    expanded = false
    create()
  }

  function onStart() { inCall = true; expanded = true; clearTimeout(idleTimer) }
  function onEnd() {
    inCall = false
    clearTimeout(endTimer)
    endTimer = setTimeout(recreate, 1500)
  }

  const onPointer = (e) => {
    const t = e.target
    const inside = !!el && (t === el || el.contains(t)) // shadow-DOM clicks retarget to host
    if (inside) { if (!inCall) { expanded = true; resetIdle() } return }
    if (expanded && !inCall) recreate()
  }

  create()
  document.addEventListener('pointerdown', onPointer, true)

  // Best-effort: enrich identity for a signed-in visitor so the agent greets by name.
  // Guest stays guest on any failure (public site → usually guest).
  let identityApplied = false
  axios.get('/api/v1/site/convai-identity')
    .then(({ data: d }) => {
      if (!d) return
      identity = {
        signed_in: d.signed_in ? 'true' : 'false',
        user_name: String(d.first_name || d.display_name || 'there'),
        user_full_name: String(d.display_name || ''),
        user_email: String(d.email || ''),
        tenant_id: String(d.tenant_id || ''),
        tenant_name: String(d.tenant_name || ''),
        has_workspace: d.has_workspace ? 'true' : 'false',
        ...timeVars(),
      }
      if (d.signed_in && !inCall && !identityApplied) { identityApplied = true; recreate() }
    })
    .catch(() => { /* stay a guest */ })

  return () => {
    document.removeEventListener('pointerdown', onPointer, true)
    clearTimeout(endTimer)
    clearTimeout(idleTimer)
    try { el && el.remove() } catch { /* noop */ }
    try { mount.remove() } catch { /* noop */ }
  }
}
